import React from 'react';

import { BasePreloader, Pagination } from '@components';
import { AuctioneerInvoiceResponse } from '@types';
import { useAuctioneersApi } from '@api/auctioneers';

import InvoicesTable from './InvoicesTable';
import { BooleanParam, NumberParam, ObjectParam, StringParam, useQueryParams, withDefault } from 'use-query-params';
import { TableColumnSortProps } from '@components/Table/TableColumn';
import InvoicesDataFilters from './InvoicesDataFilters';
import { Col, Row } from 'react-bootstrap';
import { getCurrencyFormat } from '@helpers/formats';
import fileDownload from 'js-file-download';
import { useMediaApi } from '@api/media';

interface Props {
  id: string;
  onNotFound: () => void;
}

const Invoices: React.FC<Props> = (props) => {
  const auctioneersApi = useAuctioneersApi();
  const [loaded, setLoaded] = React.useState(false);
  const [data, setData] = React.useState([] as Array<AuctioneerInvoiceResponse>);
  const [totalRows, setTotalRows] = React.useState(0);
  const [paid, setPaid] = React.useState<string | undefined>(undefined);
  const [notPaid, setNotPaid] = React.useState<string | undefined>(undefined);
  const defaultQuery = React.useMemo(
    () => ({
      step: withDefault(NumberParam, 10),
      readOnly: withDefault(BooleanParam, true),
      page: withDefault(NumberParam, 1),
      perPage: withDefault(NumberParam, 15),
      paid: withDefault(BooleanParam, undefined),
      auctionNumber: withDefault(StringParam, undefined),
      sort: withDefault(ObjectParam, {
        property: 'auctionInvoice.invoiceNumber',
        direction: 'ASC',
      }),
    }),
    [],
  );
  const [query, setQuery] = useQueryParams(defaultQuery);


  React.useEffect(() => {
    loadData();
    return () => auctioneersApi.cancelAllRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    loadData().then();
    return () => {
      auctioneersApi.cancelAllRequests();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const loadData = async () => {
    try {
      const res = await auctioneersApi.invoicesList(props.id, query, query.sort.property, query.sort.direction);
      setData(res.data?.data);
      setTotalRows(res.data.total || 0);
      setPaid(res.data.invoicePaid);
      setNotPaid(res.data.invoiceNotPaid);
      setLoaded(true);
    } catch (err: any) {
      if (auctioneersApi.isCancel(err)) {
        return;
      }
      if (err?.response?.status === 404 || err?.response?.status === 403) {
        props.onNotFound();
        return;
      }
    }
  };


  const auctioneerApi = useAuctioneersApi();
  const mediaApi = useMediaApi();

  const handleDownloadClick = async (auctionInvoiceId: number | string) => {
    const documentData = await auctioneerApi.getInvoicePDF(props.id, auctionInvoiceId);
    mediaApi
      .detail(documentData.data.data.hash, true)
      .then((res) => {
        fileDownload(res.data, documentData.data.data.originalName);
      })
      .catch((err) => {
        if (auctioneerApi.isCancel(err)) {
          return;
        }
        console.error(err);
      });
  };


  if (!loaded) {
    return (
      <div className="pt-5 pb-5 d-flex align-items-center justify-content-center">
        <BasePreloader />
      </div>
    );
  }

  return (
    <div>
      <InvoicesDataFilters
        values={query}
        onSubmit={(values) => setQuery({ ...values, page: undefined }, 'push')}
      />
      <Row>
        <Col xs={12}>
          <div className="invoice-sumarry">
            <div></div>
            <div className="items">
              <p>
                Zaplaceno doteď: <strong>{getCurrencyFormat(paid)}</strong>
              </p>
              <p>
                Nezaplaceno doteď: <strong>{getCurrencyFormat(notPaid)}</strong>
              </p>
            </div>
          </div>
        </Col>
      </Row>
      <InvoicesTable
        data={data}
        onDownloadClick={handleDownloadClick}
        sort={query.sort as TableColumnSortProps}
        onSortChange={(sort) => setQuery({ ...query, sort: sort }, 'push')}
      />
      {data.length > 0 && (
        <div className="mt-2">
          <Pagination
            perPageChoiceEnabled
            page={query.page}
            perPage={query.perPage}
            totalRows={totalRows}
            onPageChange={(page, perPage) => setQuery({ ...query, page, perPage }, 'push')}
          />
        </div>
      )}
    </div>
  );
};

export default Invoices;
